import classNames from 'classnames';
import { UnsupportedBrowserFallback } from 'components/UnsupportedBrowserFallback/UnsupportedBrowserFallback';
import { useResponsiveTheme } from 'hooks/useResponsiveTheme/useResponsiveTheme';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import './Modal.scss';
interface ModalProps {
  allowClosing?: boolean;
  children: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  size?: string;
}

const ModalContent = ({
  children,
  className = '',
  isOpen: defaultIsOpen = false,
  onClose = () => null,
  onOpen = () => null,
  size = 'small',
}: ModalProps) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const { responsive } = useResponsiveTheme();

  const handleOnClose = useCallback(() => {
    onClose();
    setIsOpen(false);
  }, [onClose]);

  const handleOnOpen = useCallback(() => {
    onOpen();
    setIsOpen(true);
  }, [onOpen]);

  useEffect(() => {
    if ((defaultIsOpen && isOpen) || defaultIsOpen !== isOpen) {
      defaultIsOpen ? handleOnOpen() : handleOnClose();
    }
  }, [defaultIsOpen, isOpen, handleOnOpen, handleOnClose]);

  return isOpen ? (
    <div className="Modal-container" style={{ zIndex: 999 }} role="dialog">
      <div
        className={classNames(
          `Modal Modal--${size} overflow-visible`,
          {
            'Modal--responsive': responsive,
          },
          className,
        )}
        data-testid="Modal"
      >
        <div className="Modal-dialog">
          <div className="Modal-content">{children}</div>
        </div>
      </div>
    </div>
  ) : null;
};

const Modal = (props: ModalProps) => (
  <ErrorBoundary FallbackComponent={UnsupportedBrowserFallback}>
    <ModalContent {...props} />
  </ErrorBoundary>
);

export { Modal };
